import React, { useEffect } from 'react';
import { Link, useLocation, useNavigationType } from 'react-router-dom';
import { useBreakpoint } from '../useBreakpoint';
import { useScope } from '../hooks/useScope';
import { NAV_SECTIONS, sectionForPath } from '../layout/sections';
import NavIcon from '../layout/NavIcon';
import { color, font, FONT_FAMILY, radius, shadow, space, TAP, weight } from '../theme';
import Icon from './Icon';

// Shell measurements (px), for page elements that stick below the top bar or must clear the nav
export const MOBILE_TOP_BAR_HEIGHT = 52;
const NAV_HEIGHT = 64;
const NAV_INSET = 12;
export const MOBILE_NAV_CLEARANCE = NAV_HEIGHT + NAV_INSET + space.xl;

// Phone and tablet shell (below the desktop breakpoint): a compact top bar and a floating
// bottom nav. Owned by the mobile-ux workstream; Layout.tsx picks this or DesktopShell.
const MobileShell = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  const { isTablet } = useBreakpoint();
  const maxW = isTablet ? 720 : 480;
  const activeKey = sectionForPath(location.pathname)?.key;
  const onSearch = location.pathname === '/search';
  const navigationType = useNavigationType();
  const scope = useScope();

  // A new page (a link or navigate()) opens at the top. Back and Forward (POP) leave
  // scrolling to the page, which restores its saved position once its data has loaded.
  useEffect(() => {
    if (navigationType === 'PUSH') window.scrollTo(0, 0);
  }, [location.pathname, navigationType]);

  return (
    <div style={{ minHeight: '100vh', background: color.page }}>
      <div style={{
        maxWidth: maxW,
        margin: '0 auto',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        fontFamily: FONT_FAMILY,
        direction: 'rtl',
        color: color.text,
      }}>

        {/* Top bar: stays in view, translucent over the content scrolling under it. The
            search page draws its own header (back + field) in its place. */}
        {!onSearch && <header style={{
          position: 'sticky',
          top: 0,
          zIndex: 100,
          height: MOBILE_TOP_BAR_HEIGHT,
          boxSizing: 'border-box',
          padding: `0 ${space.lg}px`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: 'rgba(238,242,247,0.88)',
          backdropFilter: 'saturate(180%) blur(12px)',
          WebkitBackdropFilter: 'saturate(180%) blur(12px)',
        }}>
          <Link to="/parties" aria-label="פוליטיקל, לדף הבית" style={{ fontSize: 19, fontWeight: weight.bold, color: color.text, textDecoration: 'none' }}>
            פוליטי<span style={{ color: color.primary }}>קל</span>
          </Link>
          <span style={{ display: 'flex', alignItems: 'center', gap: space.xs }}>
            <span style={{
              fontSize: font.caption,
              fontWeight: weight.medium,
              color: color.primaryInk,
              background: color.primarySoft,
              borderRadius: radius.pill,
              padding: '4px 10px',
            }}>
              כנסת {scope.knesset}
            </span>
            <Link to="/search" aria-label="חיפוש" style={{
              width: TAP,
              height: TAP,
              marginInlineEnd: -space.sm,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: color.primary,
            }}>
              <Icon name="search" size={22} strokeWidth={2} />
            </Link>
          </span>
        </header>}

        <main style={{ flex: 1, paddingBottom: MOBILE_NAV_CLEARANCE }}>
          {children}
        </main>

        {/* Bottom nav: a floating bar with the six sections from layout/sections */}
        <nav aria-label="ניווט ראשי" style={{
          position: 'fixed',
          bottom: `calc(${NAV_INSET}px + env(safe-area-inset-bottom))`,
          left: 0,
          right: 0,
          zIndex: 100,
          display: 'flex',
          justifyContent: 'center',
          pointerEvents: 'none',
        }}>
          <div style={{
            width: `calc(100% - ${NAV_INSET * 2}px)`,
            maxWidth: maxW - NAV_INSET * 2,
            height: NAV_HEIGHT,
            boxSizing: 'border-box',
            padding: `0 ${space.xs + 2}px`,
            display: 'flex',
            alignItems: 'center',
            background: color.surface,
            borderRadius: 22,
            boxShadow: shadow.floating,
            pointerEvents: 'auto',
          }}>
            {NAV_SECTIONS.map(s => {
              const active = s.key === activeKey;
              return (
                <Link
                  key={s.key}
                  to={s.to}
                  aria-current={active ? 'page' : undefined}
                  style={{
                    flex: 1,
                    minWidth: 0,
                    height: 52,
                    borderRadius: 16,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: space.xxs,
                    textDecoration: 'none',
                    fontSize: font.caption,
                    fontWeight: active ? weight.bold : weight.regular,
                    color: active ? color.primary : color.textSecondary,
                    background: active ? color.primarySoft : 'transparent',
                    transition: 'background 0.15s, color 0.15s',
                  }}
                >
                  <NavIcon name={s.icon} size={22} />
                  <span style={{ whiteSpace: 'nowrap' }}>{s.label}</span>
                </Link>
              );
            })}
          </div>
        </nav>
      </div>
    </div>
  );
};

export default MobileShell;
